import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { EncryptedStorageService } from '../services/encrypted-storage.service';

export interface AuthTokens {
    access_token: string;
    refresh_token: string;
}

export interface Role {
    name: string;
    display_name: string;
}

export interface User {
    id: string;
    username: string;
    name: string;
    email: string;
    roles: Role[];
    permissions?: string[];
}

export interface LoginRequest {
    username: string;
    password: string;
    installation_id?: string;
    device_name?: string;
    device_type?: string;
    os_version?: string;
}

export interface LoginResponse {
    status: string;
    message: string;
    data: {
        user: User;
        access_token: string;
        refresh_token: string;
        expires_at: string;
    };
}

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private readonly API_URL = `${environment.apiUrl}/v1/auth`;
    private readonly AUTH_STORAGE_KEY = 'auth';

    private _user = signal<User | null>(null);
    private _isAuthenticated = signal<boolean>(false);
    private _isLoading = signal<boolean>(false);
    private _cachedTokens: AuthTokens | null = null;

    readonly user = this._user.asReadonly();
    readonly isAuthenticated = this._isAuthenticated.asReadonly();
    readonly isLoading = this._isLoading.asReadonly();

    readonly hasPermission = computed(() => {
        const user = this._user();
        if (!user) return () => false;
        return (permission: string) => user.permissions?.includes(permission) ?? false;
    });

    private readonly http = inject(HttpClient);
    private readonly router = inject(Router);
    private readonly encryptedStorage = inject(EncryptedStorageService);

    constructor() {
        this.loadFromStorage();
    }

    private loadFromStorage(): void {
        try {
            const data = this.encryptedStorage.get<{ tokens: AuthTokens; user: User }>(
                this.AUTH_STORAGE_KEY,
            );
            if (data) {
                this._user.set(data.user);
                this._isAuthenticated.set(true);
                this._cachedTokens = data.tokens;
            } else {
                this.encryptedStorage.remove(this.AUTH_STORAGE_KEY);
            }
        } catch {
            this.encryptedStorage.remove(this.AUTH_STORAGE_KEY);
        }
    }

    login(credentials: LoginRequest): Observable<LoginResponse> {
        this._isLoading.set(true);

        return this.http.post<LoginResponse>(`${this.API_URL}/login`, credentials).pipe(
            tap((response) => {
                const tokens: AuthTokens = {
                    access_token: response.data.access_token,
                    refresh_token: response.data.refresh_token,
                };
                this.saveAuth(tokens, response.data.user);
                this._isLoading.set(false);
            }),
            catchError((error) => {
                this._isLoading.set(false);
                return throwError(() => error);
            }),
        );
    }

    logout(): Observable<void> {
        return this.http.post<void>(`${this.API_URL}/logout`, {}).pipe(
            tap(() => {
                this.clearAuth();
            }),
            catchError(() => {
                this.clearAuth();
                return throwError(() => new Error('Logout failed'));
            }),
        );
    }

    clearAuth(): void {
        this._cachedTokens = null;
        this.encryptedStorage.remove(this.AUTH_STORAGE_KEY);
        this._user.set(null);
        this._isAuthenticated.set(false);
        this.router.navigate(['/login']);
    }

    refreshToken(): Observable<LoginResponse> {
        const tokens = this.getTokens();
        if (!tokens) {
            return throwError(() => new Error('No refresh token available'));
        }

        return this.http
            .post<LoginResponse>(`${this.API_URL}/refresh`, {
                refresh_token: tokens.refresh_token,
            })
            .pipe(
                tap((response) => {
                    const newTokens: AuthTokens = {
                        access_token: response.data.access_token,
                        refresh_token: response.data.refresh_token,
                    };
                    this.saveTokens(newTokens);
                }),
            );
    }

    changePassword(oldPassword: string, newPassword: string): Observable<void> {
        return this.http.post<void>(`${this.API_URL}/change-password`, {
            old_password: oldPassword,
            new_password: newPassword,
        });
    }

    getAccessToken(): string | null {
        return this._cachedTokens?.access_token ?? null;
    }

    getRefreshToken(): string | null {
        return this._cachedTokens?.refresh_token ?? null;
    }

    private getTokens(): AuthTokens | null {
        return this._cachedTokens;
    }

    private saveTokens(tokens: AuthTokens): void {
        this._cachedTokens = tokens;
        const data = this.encryptedStorage.get<{ tokens: AuthTokens; user: User }>(
            this.AUTH_STORAGE_KEY,
        );
        if (data) {
            data.tokens = tokens;
            this.encryptedStorage.set(this.AUTH_STORAGE_KEY, data);
        }
    }

    private saveAuth(tokens: AuthTokens, user: User): void {
        this._cachedTokens = tokens;
        this.encryptedStorage.set(this.AUTH_STORAGE_KEY, { tokens, user });
        this._user.set(user);
        this._isAuthenticated.set(true);
    }
}
